/**
 * reportCommon.ts — Shared utilities for HTML report generation.
 *
 * Extracted from AutoInvestigateReport.tsx so that multiple report generators
 * (investigation report, Follow the Money report, etc.) can reuse the same
 * helpers, CSS, and document scaffolding without duplication.
 */
import { inferCategoryFromName } from '../constants/entityKeywords';

import { stampedRowsLine, stampBadgeText } from './stampedRows';

/* ── Counterparty severity (one predicate for the table header, row styling and Recommended Actions) ── */

/** True for a counterparty that must be counted as high-risk: tier high, severe or critical,
 *  a sanctions hit, or a risk score above 0.7. */
export function isHighRiskCounterparty(cp: any): boolean {
    if (!cp) return false;
    const tier = String(cp.risk_tier || '').toLowerCase();
    return tier === 'high' || tier === 'severe' || tier === 'critical'
        || cp.sanctioned === true
        || (Number(cp.risk_score) || 0) > 0.7;
}

/** Badge class for a backend risk tier. `critical` and `severe` have their own styles. */
export function counterpartyTierClass(tier: unknown): string {
    switch (String(tier || '').toLowerCase()) {
        case 'critical': return 'tier-critical';
        case 'severe': return 'tier-severe';
        case 'high': return 'tier-high';
        case 'medium': return 'tier-medium';
        default: return 'tier-low';
    }
}

/** Row class attribute (leading space, or empty) for a counterparty table row. */
export function counterpartyRowClass(cp: any): string {
    const tier = String(cp?.risk_tier || '').toLowerCase();
    if (tier === 'critical' || cp?.sanctioned === true) return ' class="highlight-critical"';
    if (tier === 'severe') return ' class="highlight-severe"';
    if (tier === 'high') return ' class="highlight"';
    return '';
}

/* ── Text helpers ── */

/** HTML-escape any value for safe embedding in report HTML */
export function esc(value: unknown): string {
    const s = value == null
        ? ''
        : typeof value === 'string'
            ? value
            : typeof value === 'object'
                ? JSON.stringify(value, null, 2)
                : String(value);

    return s
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

/** Default soft-break stride for long unbroken identifiers in printable reports. */
export const MONO_WRAP_CHUNK = 20;

/**
 * Escape a long unbroken identifier (wallet, hash, VPA, …) and insert zero-width
 * break opportunities every `chunk` characters.
 *
 * Printable report tables used to combine narrow columns with the break-all
 * word-break value, which laddered ETH/TRX addresses one glyph per line.
 * Soft breaks keep the full value intact while wrapping at readable boundaries.
 * Strings that already contain whitespace are escaped only (browsers wrap on spaces).
 *
 * Prefer this (or {@link monoSpan}) whenever embedding a full address/hash in
 * report HTML — including future report generators (R21: shared report scaffolding).
 */
export function monoWrap(text: string, chunk: number = MONO_WRAP_CHUNK): string {
    if (!text) return '';
    if (text.length <= chunk || /\s/.test(text)) return esc(text);
    const parts: string[] = [];
    for (let i = 0; i < text.length; i += chunk) {
        parts.push(esc(text.slice(i, i + chunk)));
    }
    return parts.join('&#8203;');
}

/** Convenience: escaped, soft-broken identifier inside a `.mono` span. */
export function monoSpan(text: string, chunk: number = MONO_WRAP_CHUNK): string {
    return `<span class="mono">${monoWrap(text, chunk)}</span>`;
}

/** Format a number with smart precision — avoids floating-point artifacts like 0.000017999999999999997 */
export function fmtNum(v: number, maxDecimals = 4): string {
    // Round to maxDecimals to eliminate floating-point noise
    const rounded = parseFloat(v.toFixed(maxDecimals));
    if (Number.isInteger(rounded)) return String(rounded);
    // Use toFixed but strip trailing zeros
    return rounded.toFixed(maxDecimals).replace(/\.?0+$/, '');
}

/** Format a crypto value — uses up to 8 decimals for small values to avoid truncation to 0.00 */
export function fmtCryptoVal(v: number, maxDecimals = 8): string {
    if (v === 0) return '0';
    const abs = Math.abs(v);
    const sign = v < 0 ? '-' : '';
    // For very small values, show at least the significant digits
    if (abs < 0.01 && abs > 0) {
        return v.toFixed(maxDecimals).replace(/\.?(0+)$/, '');
    }
    // Abbreviate large values: 1.2B, 842.9M, 12.5K
    if (abs >= 1e9) return `${sign}${(abs / 1e9).toFixed(1)}B`;
    if (abs >= 1e6) return `${sign}${(abs / 1e6).toFixed(1)}M`;
    if (abs >= 100_000) return `${sign}${(abs / 1e3).toFixed(1)}K`;
    // For normal values, use maxDecimals=4 with trailing zero stripping
    const rounded = parseFloat(abs.toFixed(Math.min(maxDecimals, 4)));
    if (Number.isInteger(rounded)) return `${sign}${rounded}`;
    return `${sign}${rounded.toFixed(4).replace(/\.?0+$/, '')}`;
}

/** Format a timestamp (epoch seconds, epoch ms, or ISO string) to human-readable UTC */
export function fmtTs(ts: unknown): string {
    if (ts == null) return '\u2014';
    let d: Date;
    if (typeof ts === 'number') {
        // Distinguish epoch seconds vs milliseconds: if > 1e12, treat as ms
        d = new Date(ts > 1e12 ? ts : ts * 1000);
    } else {
        d = new Date(String(ts));
    }
    if (isNaN(d.getTime())) return String(ts);
    return d.toISOString().replace('T', ' ').replace(/\.\d{3}Z$/, ' UTC');
}

/** Compact date: "14/01/2026 03:41" — for report tables where space is tight */
export function fmtTsCompact(ts: unknown): string {
    if (ts == null) return '\u2014';
    let d: Date;
    if (typeof ts === 'number') {
        d = new Date(ts > 1e12 ? ts : ts * 1000);
    } else {
        d = new Date(String(ts));
    }
    if (isNaN(d.getTime())) return String(ts);
    const dd = String(d.getUTCDate()).padStart(2, '0');
    const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
    const yy = d.getUTCFullYear();
    const hh = String(d.getUTCHours()).padStart(2, '0');
    const mi = String(d.getUTCMinutes()).padStart(2, '0');
    return `${dd}/${mm}/${yy} ${hh}:${mi}`;
}

/** Format a value for reports — smart handling of USD vs Crypto heuristics */
export function fmtReportVal(v: number, token?: string): string {
    if (v === 0) return '0';
    let t = (token || '').trim().toUpperCase();

    // R-AUDIT: Clean up placeholders
    if (t === 'CLAUDE' || t === 'UNKNOWN' || t === '\u2014') t = '';

    // R-AUDIT: Heuristic - if value is large and token is ETH or missing,
    // it's almost certainly a USD value from our enrichment feeds.
    if ((t === 'ETH' || t === '') && v > 500) {
        t = 'USD';
    }

    const fmt = fmtCryptoVal(v);
    return t ? `${fmt} ${t}` : fmt;
}

/** Truncate address for reports: first 6 + last 6 chars with ellipsis, e.g. "TDqSqu...dkhSCf" */
export function truncAddr(addr: string, head = 6, tail = 6): string {
    if (!addr || addr.length <= head + tail + 3) return addr || '';
    return `${addr.slice(0, head)}...${addr.slice(-tail)}`;
}


/** Clean up verbose entity names (e.g. ChainAbuse descriptions) */
export function cleanEntityName(name: string): string {
    if (!name) return name;
    // Strip overly verbose ChainAbuse-style report descriptions
    const colonIdx = name.indexOf(':');
    if (colonIdx > 0 && colonIdx < 30 && name.length > 50) {
        return name.slice(0, colonIdx).trim();
    }
    // Truncate any entity name > 55 chars
    return name.length > 55 ? name.slice(0, 52) + '...' : name;
}

/** Simple number format with fixed decimals */
export function fmt(v: unknown, decimals = 2): string {
    return typeof v === 'number' ? v.toFixed(decimals) : '\u2014';
}

/** Percentage format (0-1 scale input) */
export function pct(v: unknown): string {
    return typeof v === 'number' && !isNaN(v) ? `${(v * 100).toFixed(0)}%` : '\u2014';
}

/** Normalize Arkham "labeled" entity types to meaningful categories based on entity name.
 *  The name vocabulary is the shared mirror of the backend (constants/entityKeywords.ts):
 *  mixers and no-KYC swap services are resolved before the exchange role words. */
export function normalizeEntityType(entityType: string, entityName?: string): string {
    if (!entityType || entityType === 'labeled' || entityType === 'unknown') {
        return inferCategoryFromName(entityName) || entityType || 'unknown';
    }
    return entityType;
}

/** Risk badge inline CSS based on score (0-1) */
export function riskBadgeStyle(score: number): string {
    if (score >= 0.7) return 'background:#fef2f2;color:#991b1b;border:1px solid #fca5a5;';
    if (score >= 0.4) return 'background:#fffbeb;color:#92400e;border:1px solid #fcd34d;';
    return 'background:#ecfdf5;color:#065f46;border:1px solid #6ee7b7;';
}

/** Lightweight markdown to HTML for AI narrative text */
export function mdToHtml(text: string): string {
    return esc(text)
        .replace(/^### (.+)$/gm, '<h4 style="margin:12px 0 4px;font-size:13px;font-weight:700;">$1</h4>')
        .replace(/^## (.+)$/gm, '<h3 style="margin:16px 0 6px;font-size:14px;font-weight:700;border-bottom:1px solid #e2e8f0;padding-bottom:4px;">$1</h3>')
        .replace(/^# (.+)$/gm, '<h2 style="margin:20px 0 8px;font-size:16px;font-weight:700;border-bottom:2px solid #cbd5e1;padding-bottom:6px;">$1</h2>')
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        .replace(/^- (.+)$/gm, '<li style="margin-left:16px;margin-bottom:2px;">$1</li>')
        .replace(/\n\n/g, '</p><p style="margin:8px 0;">')
        .replace(/\n/g, '<br/>');
}

/* ── Projection contract: shared types across reports ── */

/**
 * Counterparty primitives emitted by the backend (counterparty_triage,
 * exposure_analyzer, trace endpoints). Every report — Auto Investigation,
 * Follow the Money, LINKS — reads the same fields and applies one of the
 * projection functions below to compose its view. Adding ad-hoc sort logic
 * inside a report renderer is forbidden — extend the projection here.
 */
export interface CounterpartyPrimitive {
    address: string;
    chain?: string;
    entity_name?: string;
    entity_type?: string;
    entity_known?: boolean;
    risk_tier?: 'critical' | 'severe' | 'high' | 'medium' | 'low' | string;
    risk_score?: number;
    sanctioned?: boolean;
    is_actionable?: boolean;
    actionable_jurisdiction?: 'india_direct' | 'india_via_partner' | 'mlat_required' | 'non_actionable' | string;
    evidence_strength?: 'direct' | 'one_hop' | 'multi_hop' | string;
    primary_volume?: number;
    primary_token?: string;
    total_volume?: number;
    volume_in?: number;
    volume_out?: number;
    is_spam_token?: boolean;
    [k: string]: unknown;
}

/**
 * Notable callout — a specific finding worth elevating to the exec summary.
 * Emitted by signal_assembler (entity + exposure families today). Renderers
 * surface these in the "Notable Findings" section of the executive summary
 * regardless of magnitude — a $145k Tether-blacklisted address belongs in
 * the summary, not buried in Appendix C.
 */
export interface NotableCallout {
    kind: string;                  // 'sanctions_root' | 'root_high_risk_entity' | 'exposure_high_risk_category' | ...
    address?: string;
    entity_name?: string;
    entity_category?: string;
    category?: string;
    direction?: string;
    pct?: number;
    reason: string;                // human-readable, ready for direct render
    magnitude?: 'max' | 'high' | 'medium' | 'low';
    [k: string]: unknown;
}

/* ── Risk tier weights ── */

/** Numeric weight for risk_tier sorting. Higher = more severe. */
export const RISK_TIER_WEIGHT: Record<string, number> = {
    critical: 5,
    severe: 4,
    high: 3,
    medium: 2,
    low: 1,
};

export function riskTierWeight(tier: string | undefined): number {
    if (!tier) return 0;
    return RISK_TIER_WEIGHT[tier.toLowerCase()] ?? 0;
}

/* ── Jurisdiction priority + copy ── */

/** Priority for sorting actionable counterparties — lower = sorts first. */
export const JURISDICTION_PRIORITY: Record<string, number> = {
    india_direct: 0,
    india_via_partner: 1,
    mlat_required: 2,
    non_actionable: 99,
};

/** Pretty label for the jurisdiction bucket — used in tables and prose. */
export function formatJurisdiction(j: string | undefined): string {
    switch ((j || '').toLowerCase()) {
        case 'india_direct': return 'India (FIU-IND, direct)';
        case 'india_via_partner': return 'India (via local entity)';
        case 'mlat_required': return 'International (MLAT required)';
        case 'non_actionable': return 'Non-actionable';
        default: return j || '—';
    }
}

/* ── Projection functions: one source of truth for ordering ── */

/**
 * Sort counterparties for Section 94 / legal-action view. Filters out
 * non-actionable entries (mixers, DEXes, unknowns) and orders by
 * (jurisdiction priority, volume desc) so an Indian exchange with ₹10k
 * sorts above a US exchange with ₹1M — because Section 94 to WazirX lands
 * in 48h and Coinbase needs MLAT (12-18 months).
 *
 * Used by: Recommended Actions section in any report.
 */
export function sortForLegalAction<T extends CounterpartyPrimitive>(counterparties: T[]): T[] {
    const actionable = counterparties.filter(c => c.is_actionable === true && !c.is_spam_token);
    return [...actionable].sort((a, b) => {
        const ja = JURISDICTION_PRIORITY[a.actionable_jurisdiction || 'non_actionable'] ?? 99;
        const jb = JURISDICTION_PRIORITY[b.actionable_jurisdiction || 'non_actionable'] ?? 99;
        if (ja !== jb) return ja - jb;
        const va = Number(a.primary_volume ?? a.total_volume ?? 0);
        const vb = Number(b.primary_volume ?? b.total_volume ?? 0);
        return vb - va;
    });
}

/**
 * Sort counterparties for narrative / understanding view. No actionability
 * filter — show everything. Order by composite (risk_tier, volume,
 * evidence_strength). Magnitude matters within tier (raw, no log dampening
 * — a $474k entity sorts above a $14k entity at the same risk tier) but a
 * SEVERE always sorts above a HIGH regardless of volume.
 *
 * Used by: Counterparties table, Key Findings, Top Counterparties.
 */
export function sortForNarrativeImpact<T extends CounterpartyPrimitive>(counterparties: T[]): T[] {
    const evidenceWeight = (s?: string) => s === 'direct' ? 3 : s === 'one_hop' ? 2 : s === 'multi_hop' ? 1 : 0;
    return [...counterparties].sort((a, b) => {
        // Spam-tokens always last (regardless of volume/risk)
        const sa = a.is_spam_token ? 1 : 0;
        const sb = b.is_spam_token ? 1 : 0;
        if (sa !== sb) return sa - sb;
        // Risk tier strictly dominates
        const ra = riskTierWeight(a.risk_tier as string);
        const rb = riskTierWeight(b.risk_tier as string);
        if (ra !== rb) return rb - ra;
        // Then evidence strength
        const ea = evidenceWeight(a.evidence_strength as string);
        const eb = evidenceWeight(b.evidence_strength as string);
        if (ea !== eb) return eb - ea;
        // Then raw volume
        const va = Number(a.primary_volume ?? a.total_volume ?? 0);
        const vb = Number(b.primary_volume ?? b.total_volume ?? 0);
        return vb - va;
    });
}

/**
 * Pick "notable callouts" worth surfacing in the executive summary. Two
 * sources: (1) explicit notable_callouts emitted by signal_assembler family
 * results, (2) counterparties at SEVERE/CRITICAL risk tier (these always
 * deserve summary mention regardless of volume — a $145k Tether-blacklisted
 * address shouldn't get buried just because there's a $1M Bybit hit).
 *
 * Returns at most `limit` items (default 5) deduplicated by (kind, address).
 *
 * Used by: Executive Summary "Notable Findings" block in any report.
 */
export function selectNotableCallouts(
    counterparties: CounterpartyPrimitive[] | null | undefined,
    signalCallouts: NotableCallout[] | null | undefined,
    limit: number = 5,
): NotableCallout[] {
    const seen = new Set<string>();
    const out: NotableCallout[] = [];

    const push = (c: NotableCallout) => {
        const key = `${c.kind}::${c.address || c.category || c.reason}`;
        if (seen.has(key)) return;
        seen.add(key);
        out.push(c);
    };

    // 1. Signal-emitted callouts first (highest authority — backend explicitly marked).
    for (const c of signalCallouts || []) {
        push(c);
        if (out.length >= limit) return out;
    }

    // 2. Counterparty-derived callouts: everything at SEVERE/CRITICAL.
    for (const cp of counterparties || []) {
        if (cp.is_spam_token) continue;
        const tier = (cp.risk_tier as string || '').toLowerCase();
        if (tier !== 'critical' && tier !== 'severe') continue;
        const vol = Number(cp.primary_volume ?? cp.total_volume ?? 0);
        push({
            kind: cp.sanctioned ? 'counterparty_sanctioned' : 'counterparty_high_risk',
            address: cp.address,
            entity_name: cp.entity_name,
            entity_category: cp.entity_type,
            reason: cp.sanctioned
                ? `Sanctioned counterparty (${cp.entity_name || cp.entity_type || 'unknown entity'}) — ${vol > 0 ? `${fmtCryptoVal(vol)} ${cp.primary_token || ''}` : 'flagged'}`
                : `${tier.toUpperCase()} risk counterparty: ${cp.entity_name || cp.entity_type || 'unknown'}${vol > 0 ? ` (${fmtCryptoVal(vol)} ${cp.primary_token || ''})` : ''}`,
            magnitude: cp.sanctioned ? 'max' : 'high',
        });
        if (out.length >= limit) return out;
    }

    return out;
}

/* ── NOT TESTED reason → user copy ── */

/** Map backend not_tested_reason to user-facing copy for the report. */
export function formatNotTestedReason(reason: string | null | undefined, context?: string): string {
    const r = (reason || '').toLowerCase();
    const ctx = context ? `${context}: ` : '';
    switch (r) {
        case 'timeout':
            return `${ctx}Analysis exceeded its time budget and was skipped to keep the report timely. The full assessment is available on demand via the Investigation Workbench.`;
        case 'no_data':
            return `${ctx}Insufficient data to run this check (e.g. transaction set too small, no graph context).`;
        case 'fetcher_error':
            return `${ctx}Upstream data source was unavailable when this check ran. Retry from the Workbench may succeed.`;
        case 'exception':
            return `${ctx}This check encountered an unexpected error during execution. Engineering has been notified.`;
        case 'skipped':
            return `${ctx}Check intentionally skipped (e.g. graph too large, address out of scope).`;
        case 'no_feed_for_chain':
            return `${ctx}No external intelligence feed covers this chain, so this family was not checked. This is not a clean result.`;
        case 'manual_only':
            return `${ctx}This check requires analyst review and is not automated. See methodology notes.`;
        default:
            return `${ctx}Not assessed during this report run.`;
    }
}

/* ── Provider health (shared by Coverage and Limitations) ── */

/**
 * Provider call table for this investigation, or an explicit line saying provider
 * status was not recorded. Never silent: an empty table would read as "all healthy".
 */
export function renderProviderHealthBlock(providerHealth: Record<string, any> | null | undefined): string {
    if (!providerHealth || typeof providerHealth !== 'object' || Object.keys(providerHealth).length === 0) {
        return '<p style="font-size:12px;color:#64748b;margin:0;">Provider status was not recorded for this run, so the report cannot say which data providers answered or failed.</p>';
    }
    const rows = Object.entries(providerHealth).map(([provider, stats]: [string, any]) => {
        const ok = stats?.ok ?? 0;
        const failed = stats?.failed ?? 0;
        const total = ok + failed;
        const statusIcon = failed === 0 ? '<span style="color:#16a34a;">OK</span>' : (ok === 0 ? '<span style="color:#dc2626;">Failed</span>' : '<span style="color:#d97706;">Degraded</span>');
        return `<tr><td style="padding:3px 8px;">${esc(provider)}</td><td style="padding:3px 8px;text-align:center;">${total}</td><td style="padding:3px 8px;text-align:center;">${ok}</td><td style="padding:3px 8px;text-align:center;">${failed}</td><td style="padding:3px 8px;">${statusIcon}</td></tr>`;
    }).join('');
    const anyFailed = Object.values(providerHealth).some((st: any) => (st?.failed ?? 0) > 0);
    return `
    <div style="padding:10px 14px;background:${anyFailed ? '#fef2f2' : '#f0fdf4'};border:1px solid ${anyFailed ? '#fca5a5' : '#86efac'};border-radius:4px;">
        <strong style="font-size:12px;">Provider Health &mdash; This Investigation</strong>
        <table class="data" style="font-size:11px;margin-top:6px;">
            <thead><tr><th style="text-align:left;">Provider</th><th>Total Calls</th><th>Succeeded</th><th>Failed</th><th style="text-align:left;">Status</th></tr></thead>
            <tbody>${rows}</tbody>
        </table>
        ${anyFailed ? '<p style="font-size:10px;color:#991b1b;margin:6px 0 0;">Some data providers were unavailable during this investigation. Analysis completeness may be affected.</p>' : ''}
    </div>`;
}

/* ── Path self-loop filter (defensive) ── */

/**
 * Defensive frontend filter — drop paths where root appears in the middle.
 * Backend graph_service._filter_self_loops is the authoritative filter, but
 * this lets us belt-and-braces the renderer in case stale snapshots or
 * older API responses slip through.
 */
export function dedupePathSelfLoops<T extends { path?: string[]; addresses?: string[] }>(
    paths: T[],
    rootAddress: string,
): T[] {
    const root = (rootAddress || '').toLowerCase();
    const norm = (a: string) => (a || '').toLowerCase();
    return (paths || []).filter(p => {
        const addrs = (p.path || p.addresses || []).map(norm);
        if (addrs.length < 2) return true;
        const middle = addrs.slice(1, -1);
        if (middle.includes(root)) return false;
        for (let i = 0; i < addrs.length - 1; i++) {
            if (addrs[i] === addrs[i + 1]) return false;
        }
        return true;
    });
}

/* ── Evidence Numbering ── */

/** Sequential evidence counter — generates E-1, E-2, etc. for court-admissible referencing */
export class EvidenceCounter {
    private _count = 0;
    next(): string {
        this._count++;
        return `<span class="evidence-id">E-${this._count}</span>`;
    }
    get count(): number {
        return this._count;
    }
    reset(): void {
        this._count = 0;
    }
}

/* ── Evidence tiers, legend and disclaimer (one source for every report) ── */

export type EvidenceTier = 1 | 2 | 3;

/** The three confidence tiers every report uses. Text is shared, never re-worded per report. */
export const EVIDENCE_TIERS: ReadonlyArray<{ tier: EvidenceTier; label: string; short: string; description: string }> = [
    { tier: 1, label: 'Tier 1 — On-chain Fact', short: 'On-chain fact', description: 'Verifiable by anyone with blockchain access (transactions, balances, timestamps).' },
    { tier: 2, label: 'Tier 2 — Analytical Inference', short: 'Analytical inference', description: 'Algorithmic, reproducible, defensible (risk scores, pattern detection, velocity metrics).' },
    { tier: 3, label: 'Tier 3 — AI-generated Hypothesis', short: 'AI-generated hypothesis', description: 'Requires human validation. AI narratives are indicative, not evidentiary.' },
];

/**
 * The confidence-tier legend box. `extraHtml` is appended inside the box after
 * the tier lines (the LE report adds its AI-contribution list there).
 */
export function buildEvidenceTierLegend(extraHtml = ''): string {
    const lines = EVIDENCE_TIERS.map((t) => `<strong>${t.label}:</strong> ${t.description}`).join('<br/>\n        ');
    return `<div class="evidence-tier-legend" data-testid="evidence-tier-legend" style="margin-top:12px;padding:10px 14px;background:#f8fafc;border:1px solid #e2e8f0;border-radius:4px;font-size:11px;line-height:1.6;color:#475569;">
        <strong>Confidence Tiers:</strong><br/>
        ${lines}${extraHtml}
    </div>`;
}

/** Inline tier tag for a section or a fact: "Tier 1 · On-chain fact". */
export function buildTierTag(tier: EvidenceTier): string {
    const t = EVIDENCE_TIERS.find((x) => x.tier === tier);
    return t ? `<span class="tier-tag tier-tag-${tier}" title="${esc(t.description)}">Tier ${tier} · ${esc(t.short)}</span>` : '';
}

/** Closing disclaimer shared by the LE report and the case report. */
export const REPORT_DISCLAIMER_TEXT = 'This report is generated by automated cryptocurrency forensic analysis tools. AI-generated intelligence'
    + ' notes (Tier 3) are indicative and must be independently verified before any legal or enforcement action.'
    + ' On-chain data (Tier 1) is publicly verifiable. Analytical inferences (Tier 2) are reproducible given'
    + ' the same input data. This report does not constitute a formal legal opinion. All findings should be'
    + ' corroborated with exchange records, KYC data, and other investigative sources.';

export function buildReportDisclaimer(): string {
    return `<div class="disclaimer" data-testid="report-disclaimer">
        ${REPORT_DISCLAIMER_TEXT}<br/><br/>
        Generated by Outris Crypto Investigation Platform
    </div>`;
}

/**
 * A collapsed code block with a Copy button (screen only). Used for diagram
 * sources the officer redraws in a flowchart editor. `id` must be unique in
 * the document.
 */
export function buildCollapsedCodeBlock(id: string, summary: string, code: string): string {
    const safeId = id.replace(/[^a-zA-Z0-9_-]/g, '_');
    return `<details class="code-block" data-testid="code-block-${esc(safeId)}">
        <summary>${esc(summary)}</summary>
        <div class="code-block-actions screen-only"><button type="button" class="btn-shell" onclick="window.__reportCopy && window.__reportCopy('${safeId}', this)">Copy</button></div>
        <pre class="code" id="${safeId}"><code>${esc(code)}</code></pre>
    </details>`;
}

/* ── CSS ── */

/** Shared printable + interactive report shell (screen TOC / collapse; print expands all). */
export const REPORT_CSS = `
:root {
    --ink: #0f172a;
    --muted: #64748b;
    --rule: #e2e8f0;
    --surface: #f8fafc;
    --accent: #2563eb;
    --accent-hover: #1d4ed8;
    --escalate: #dc2626;
    --investigate: #f59e0b;
    --monitor: #64748b;
    --page-max: 920px;
    --toc-width: 210px;
    --toolbar-h: 48px;
}
* { box-sizing: border-box; margin: 0; padding: 0; }
body {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
    font-size: 13px; line-height: 1.55; color: #1e293b; background: #f1f5f9;
    -webkit-print-color-adjust: exact; print-color-adjust: exact;
}
.toolbar {
    position: sticky; top: 0; z-index: 20;
    background: #fff; border-bottom: 1px solid var(--rule);
    padding: 8px 20px; display: flex; align-items: center; justify-content: space-between; gap: 12px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.06); min-height: var(--toolbar-h);
}
.toolbar-title { font-size: 13px; font-weight: 600; color: #475569; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.toolbar-actions { display: flex; gap: 6px; align-items: center; flex-wrap: wrap; justify-content: flex-end; }
.btn-print, .btn-download, .btn-shell {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 6px 12px; border-radius: 4px; border: none; cursor: pointer;
    font-size: 12px; font-weight: 600;
}
.btn-print { color: #fff; background: var(--accent); }
.btn-print:hover { background: var(--accent-hover); }
.btn-download, .btn-shell { color: #334155; background: #e2e8f0; border: 1px solid #cbd5e1; }
.btn-download:hover, .btn-shell:hover { background: #cbd5e1; }
.report-shell { max-width: calc(var(--page-max) + var(--toc-width) + 48px); margin: 20px auto; padding: 0 16px 32px; }
.report-layout { display: grid; grid-template-columns: var(--toc-width) minmax(0, 1fr); gap: 20px; align-items: start; }
.report-toc-rail {
    position: sticky; top: calc(var(--toolbar-h) + 12px);
    max-height: calc(100vh - var(--toolbar-h) - 24px); overflow: auto;
    background: #fff; border: 1px solid var(--rule); border-radius: 8px; padding: 12px 10px;
}
.report-toc-rail .toc-label {
    font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px;
    color: var(--muted); margin: 0 6px 8px;
}
.report-toc-rail nav a {
    display: block; padding: 6px 8px; margin: 1px 0; border-radius: 4px;
    font-size: 11.5px; line-height: 1.35; color: #334155; text-decoration: none;
    border-left: 2px solid transparent;
}
.report-toc-rail nav a:hover { background: var(--surface); color: var(--ink); }
.report-toc-rail nav a.is-active {
    background: #eff6ff; color: var(--accent); border-left-color: var(--accent); font-weight: 600;
}
.report-main {
    background: #fff; border-radius: 8px; box-shadow: 0 4px 12px rgba(0,0,0,0.06);
    border: 1px solid var(--rule); padding: 36px 40px; min-width: 0;
}
.page { max-width: var(--page-max); margin: 24px auto; background: #fff;
    border-radius: 8px; box-shadow: 0 4px 12px rgba(0,0,0,0.08); padding: 48px; }
.report-main .page { max-width: none; margin: 0; box-shadow: none; border: none; border-radius: 0; padding: 0; }
.masthead { margin-bottom: 28px; padding-bottom: 16px; border-bottom: 2px solid var(--ink); }
.masthead .brand {
    font-size: 10px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase;
    color: var(--muted); margin-bottom: 8px;
}
.masthead h1 { font-size: 20px; font-weight: 700; letter-spacing: 0.5px; color: var(--ink); line-height: 1.25; }
.masthead .subtitle { font-size: 11px; text-transform: uppercase; letter-spacing: 1.5px; color: var(--muted); margin-top: 6px; font-weight: 600; }
.masthead .date { font-size: 11px; color: var(--muted); margin-top: 8px; }
.masthead .data-mode {
    display: inline-block; margin-top: 8px; padding: 2px 8px; border-radius: 999px;
    font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px;
    background: #e2e8f0; color: #334155;
}
.masthead .data-mode.live { background: #dcfce7; color: #166534; }
.masthead .data-mode.cached { background: #fef3c7; color: #92400e; }
.masthead .data-mode.mock { background: #fee2e2; color: #991b1b; }
.header { text-align: center; margin-bottom: 32px; }
.header h1 { font-size: 18px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; color: var(--ink); }
.header .subtitle { font-size: 10px; text-transform: uppercase; letter-spacing: 3px; color: #94a3b8; margin-top: 4px; font-weight: 600; }
.header .date { font-size: 11px; color: #94a3b8; margin-top: 8px; }
.meta-box { background: var(--surface); border: 1px solid var(--rule); border-radius: 6px; padding: 16px; margin-bottom: 20px; }
.meta-box h2 { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 1.5px; color: var(--muted); margin-bottom: 8px; }
.meta-table td { padding: 2px 0; font-size: 13px; }
.meta-table .label { font-weight: 600; color: #475569; padding-right: 16px; width: 110px; }
.meta-table .value { color: var(--ink); }
.mono, .summary-value.mono {
    font-family: 'SF Mono', 'Cascadia Code', 'Consolas', monospace;
    overflow-wrap: anywhere; word-break: normal; line-height: 1.45;
}
.assessment-box { border-radius: 6px; padding: 16px; margin-bottom: 20px; border-width: 2px; border-style: solid; }
.assessment-box.escalate { background: #fef2f2; border-color: #fca5a5; }
.assessment-box.investigate { background: #fffbeb; border-color: #fcd34d; }
.assessment-box.monitor { background: var(--surface); border-color: #cbd5e1; }
.badge { display: inline-block; padding: 4px 12px; border-radius: 4px; font-size: 12px; font-weight: 700; text-transform: uppercase; }
.badge-escalate { background: var(--escalate); color: #fff; }
.badge-investigate { background: var(--investigate); color: #fff; }
.badge-monitor { background: var(--monitor); color: #fff; }
.section, .report-section { margin-bottom: 16px; }
.section h2, .report-section-summary {
    font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; color: var(--ink);
    border-bottom: 2px solid var(--rule); padding-bottom: 6px; margin-bottom: 10px;
}
.report-section {
    border: 1px solid var(--rule); border-radius: 6px; background: #fff; overflow: hidden;
}
.report-section-summary {
    list-style: none; cursor: pointer; user-select: none;
    display: flex; align-items: center; gap: 8px;
    margin: 0; padding: 10px 12px; border-bottom: 1px solid transparent;
    background: var(--surface);
}
.report-section-summary::-webkit-details-marker { display: none; }
.report-section-summary::before {
    content: ''; width: 0; height: 0;
    border-left: 5px solid #64748b; border-top: 4px solid transparent; border-bottom: 4px solid transparent;
    transition: transform 0.15s ease;
}
.report-section[open] > .report-section-summary {
    border-bottom-color: var(--rule);
}
.report-section[open] > .report-section-summary::before { transform: rotate(90deg); }
.report-section-title { flex: 1; }
.report-section-body { padding: 12px 14px 16px; }
.section h2 {
    font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; color: var(--ink);
    border-bottom: 2px solid var(--rule); padding-bottom: 4px; margin-bottom: 8px;
}
table.data { width: 100%; border-collapse: collapse; font-size: 12px; border: 1px solid #cbd5e1; }
table.data th { text-align: left; padding: 6px 12px; background: #f1f5f9; border-bottom: 1px solid #cbd5e1; font-weight: 600; color: #334155; }
table.data th.right { text-align: right; }
table.data td {
    padding: 6px 12px; border-bottom: 1px solid var(--rule); color: #334155;
    vertical-align: top; overflow-wrap: anywhere; word-break: normal;
}
table.data td.right { text-align: right; }
table.data td.muted { color: #94a3b8; }
.ats-panel { margin: 12px 0; padding: 12px 14px; border: 1px solid var(--rule); border-left: 3px solid var(--monitor); border-radius: 6px; background: var(--surface); }
.ats-panel.is-hit { border-left-color: var(--escalate); }
.ats-panel.is-not-tested { border-left-color: var(--investigate); }
.ats-status { font-weight: 600; margin: 8px 0; color: var(--ink); }
.ats-status.is-not-tested { color: #92400e; }
.ats-chips { margin: 8px 0; }
.ats-chip { display: inline-block; margin: 0 6px 6px 0; padding: 2px 10px; border-radius: 4px; border: 1px solid #cbd5e1; background: #fff; color: var(--muted); font-size: 11px; font-weight: 600; }
.ats-chip.is-hit { background: #fef2f2; border-color: #fca5a5; color: #991b1b; }
table.data tr.highlight { background: #fef3c7; }
table.data tr.highlight-severe { background: #ffedd5; box-shadow: inset 3px 0 0 #ea580c; }
table.data tr.highlight-critical { background: #fee2e2; box-shadow: inset 3px 0 0 #b91c1c; }
.tier-severe { background: #ffedd5; color: #9a3412; border: 1px solid #fdba74; }
.tier-critical { background: #b91c1c; color: #ffffff; border: 1px solid #7f1d1d; }
@media screen {
    table.data tbody tr:nth-child(even) { background: #fafbfc; }
    table.data tbody tr:hover { background: #f1f5f9; }
}
.risk-badge { display: inline-block; padding: 2px 8px; border-radius: 3px; font-size: 11px; font-weight: 600; }
.evidence-id {
    display: inline-block; padding: 1px 6px; border-radius: 3px; font-size: 9px; font-weight: 700;
    font-family: 'SF Mono', 'Cascadia Code', 'Consolas', monospace;
    background: #dbeafe; color: #1e40af; border: 1px solid #93c5fd;
    margin-right: 4px; vertical-align: middle; letter-spacing: 0.5px;
}
.tier-tag {
    display: inline-block; padding: 1px 6px; border-radius: 3px; font-size: 9.5px; font-weight: 700;
    margin: 0 4px 0 0; vertical-align: middle; border: 1px solid;
    -webkit-print-color-adjust: exact; print-color-adjust: exact;
}
.tier-tag-1 { background: #ecfdf5; color: #065f46; border-color: #6ee7b7; }
.tier-tag-2 { background: #eff6ff; color: #1e40af; border-color: #93c5fd; }
.tier-tag-3 { background: #fef3c7; color: #92400e; border-color: #fcd34d; }
.disclaimer {
    margin-top: 40px; padding-top: 16px; border-top: 1px solid #e2e8f0;
    text-align: center; font-size: 10px; color: #94a3b8; line-height: 1.6;
}
details.code-block { margin: 6px 0 12px; }
details.code-block > summary { cursor: pointer; font-size: 11px; color: var(--muted); }
.code-block-actions { margin: 4px 0; }
pre.code { background: #0f172a; color: #e2e8f0; padding: 10px; border-radius: 4px; overflow-x: auto; font-size: 11px; white-space: pre; }
.gold-path-row { background: #fffbeb; border-left: 3px solid #f59e0b; }
.exchange-badge { background: #dbeafe; color: #1d4ed8; padding: 2px 6px; border-radius: 3px; font-size: 10px; font-weight: 600; }
.muted { color: #94a3b8; }
.not-run { color: #94a3b8; font-style: italic; }
    .demo-watermark { background: #f59e0b; color: #000; text-align: center; padding: 6px; font-size: 11px; font-weight: 700; letter-spacing: 1px; }
.toc {
    background: var(--surface); border: 1px solid var(--rule); border-radius: 6px;
    padding: 16px 24px; margin-bottom: 24px;
}
.toc h2 {
    font-size: 12px; font-weight: 700; text-transform: uppercase;
    letter-spacing: 1px; color: var(--ink); margin-bottom: 10px;
}
.toc ol { margin: 0; padding-left: 20px; font-size: 12px; line-height: 2; }
.toc a { color: var(--accent); text-decoration: none; }
.toc a:hover { text-decoration: underline; }
.print-toc { display: none; }
.screen-only { }
/* NOTICE12: rows folded under a victim-path summary line; opened by its control, always shown in print */
tr.victim-folded { display: none; }
tr.victim-folded.open { display: table-row; }
@media (max-width: 960px) {
    .report-layout { grid-template-columns: 1fr; }
    .report-toc-rail { position: static; max-height: none; }
}
@media print {
    .toolbar, .btn-shell, .report-toc-rail, .screen-only { display: none !important; }
    tr.victim-folded { display: table-row !important; }
    .print-toc { display: block !important; }
    body { background: #fff; }
    .report-shell { max-width: none; margin: 0; padding: 0; }
    .report-layout { display: block; }
    .report-main, .page {
        box-shadow: none; margin: 0; padding: 24px; max-width: none;
        border: none; border-radius: 0;
    }
    /* No keep-together on a section or a table: either can run to hundreds of pages, and a browser asked to fit one on a page never finishes paginating. Only small blocks (a row, a figure, a notice) are kept whole. */
    /* overflow: hidden here would also freeze the layout width at the first page's, clipping a landscape appendix. */
    .section, .report-section { border: none; overflow: visible; }
    .section h2, .report-section-summary, .section h3, .section h4 { break-after: avoid; page-break-after: avoid; }
    table.data tr { break-inside: avoid; page-break-inside: avoid; }
    table.data thead { display: table-header-group; }
    .report-section-summary {
        cursor: default; background: transparent; border-bottom: 2px solid var(--rule);
        padding: 0 0 4px; margin-bottom: 8px;
    }
    .report-section-summary::before { display: none; }
    .report-section-body { padding: 0; display: block !important; }
    details.report-section > .report-section-body { display: block !important; }
    table.data tbody tr:nth-child(even), table.data tbody tr:hover { background: transparent; }
    @page { size: A4; margin: 20mm 15mm 28mm 15mm; }
    .outris-brand-watermark {
        position: fixed; inset: 0; pointer-events: none; z-index: 0;
        display: flex; align-items: center; justify-content: center;
        opacity: 0.055; font-size: 64px; font-weight: 700; letter-spacing: 0.35em;
        transform: rotate(-28deg); color: #0f172a; user-select: none;
        -webkit-print-color-adjust: exact; print-color-adjust: exact;
    }
    .outris-report-footer {
        margin-top: 28px; padding: 10px 12px 0; border-top: 1px solid #e2e8f0;
        text-align: center; font-size: 10px; color: #64748b; line-height: 1.5;
        position: relative; z-index: 1;
    }
}
.outris-brand-watermark {
    position: fixed; inset: 0; pointer-events: none; z-index: 0;
    display: flex; align-items: center; justify-content: center;
    opacity: 0.05; font-size: 72px; font-weight: 700; letter-spacing: 0.35em;
    transform: rotate(-28deg); color: #0f172a; user-select: none;
}
.outris-report-footer {
    margin-top: 28px; padding: 10px 12px 0; border-top: 1px solid #e2e8f0;
    text-align: center; font-size: 10px; color: #64748b; line-height: 1.5;
    position: relative; z-index: 1;
}
.report-main, .page { position: relative; z-index: 1; }
`;

/** Inline script: expand/collapse all, print expands sections, TOC active highlight. */
export const REPORT_SHELL_SCRIPT = `(function(){
  function allSections(){ return Array.prototype.slice.call(document.querySelectorAll('details.report-section')); }
  function setOpen(open){ allSections().forEach(function(d){ d.open = open; }); }
  window.__reportExpandAll = function(){ setOpen(true); };
  window.__reportCollapseAll = function(){ setOpen(false); };
  window.__reportPrint = function(){
    setOpen(true);
    setTimeout(function(){ window.print(); }, 30);
  };
  window.__reportCopy = function(id, btn){
    var el = document.getElementById(id);
    if (!el) return;
    var text = el.innerText || el.textContent || '';
    function selectIt(){
      var r = document.createRange(); r.selectNodeContents(el);
      var s = window.getSelection(); if (s) { s.removeAllRanges(); s.addRange(r); }
    }
    function said(msg){ if (btn) { var t = btn.textContent; btn.textContent = msg; setTimeout(function(){ btn.textContent = t; }, 1800); } }
    var details = el.closest ? el.closest('details') : null;
    if (details) details.open = true;
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(function(){ said('Copied'); }, function(){ selectIt(); said('Selected: press Ctrl+C'); });
        return;
      }
    } catch (e) { /* fall through */ }
    selectIt();
    try { said(document.execCommand('copy') ? 'Copied' : 'Selected: press Ctrl+C'); } catch (e) { said('Selected: press Ctrl+C'); }
  };
  // Same-document anchors scroll inside the report and never navigate. In a srcdoc frame a
  // fragment link resolves against the parent page URL, so a default click would load the
  // app shell into the sandboxed frame (origin null) and blank the report.
  document.addEventListener('click', function(ev){
    if (ev.defaultPrevented || ev.button || ev.metaKey || ev.ctrlKey || ev.shiftKey || ev.altKey) return;
    var t = ev.target;
    var a = t && t.closest ? t.closest('a[href^="#"]') : null;
    if (!a) return;
    var id = (a.getAttribute('href') || '').slice(1);
    try { id = decodeURIComponent(id); } catch (e) { /* keep raw */ }
    ev.preventDefault();
    if (!id) { window.scrollTo(0, 0); return; }
    var el = document.getElementById(id);
    if (!el) return;
    var d = el.closest ? el.closest('details') : null;
    while (d) { d.open = true; d = d.parentElement && d.parentElement.closest ? d.parentElement.closest('details') : null; }
    el.scrollIntoView({ block: 'start' });
  }, true);
  var links = Array.prototype.slice.call(document.querySelectorAll('[data-report-toc] a[href^="#"]'));
  if (!links.length || !('IntersectionObserver' in window)) return;
  var map = {};
  links.forEach(function(a){
    var id = (a.getAttribute('href') || '').slice(1);
    if (id) map[id] = a;
  });
  var obs = new IntersectionObserver(function(entries){
    entries.forEach(function(en){
      if (!en.isIntersecting) return;
      var id = en.target.id;
      links.forEach(function(a){ a.classList.remove('is-active'); });
      if (map[id]) map[id].classList.add('is-active');
    });
  }, { rootMargin: '-20% 0px -65% 0px', threshold: 0.01 });
  Object.keys(map).forEach(function(id){
    var el = document.getElementById(id);
    if (el) obs.observe(el);
  });
})();`;

export type ReportTocEntry = { id: string; label: string };

/* ── Document scaffolding ── */

/** Build the sticky toolbar with Save HTML / Save Text / Print (+ expand/collapse). */
export function buildReportToolbar(title: string, filename: string, opts?: { readOnly?: boolean; printViaParent?: boolean }): string {
    const safe = filename.replace(/[^a-zA-Z0-9_\-]/g, '_');
    // A shared (read-only) view keeps navigation and print but offers no "Save HTML" / "Save Text" file copy.
    const saves = opts?.readOnly ? '' : `
            <button type="button" class="btn-download" onclick="(function(){var a=document.createElement('a');a.href='data:text/html;charset=utf-8,'+encodeURIComponent(document.documentElement.outerHTML);a.download='${safe}.html';a.click()})()">Save HTML</button>
            <button type="button" class="btn-download" onclick="(function(){var t=(document.querySelector('.report-main')||document.querySelector('.page')||document.body).innerText;var a=document.createElement('a');a.href='data:text/plain;charset=utf-8,'+encodeURIComponent(t);a.download='${safe}.txt';a.click()})()">Save Text</button>`;
    // An embedding page that owns a Print dialog (the case report) is asked first; a standalone copy prints at once.
    const printCall = opts?.printViaParent
        ? "if(window.parent&&window.parent!==window){window.parent.postMessage({type:'case-report-print'},'*')}else{(window.__reportPrint||window.print)()}"
        : '(window.__reportPrint||window.print)()';
    // An embedding page with a dialog also hears Escape pressed inside this frame (the frame is sandboxed, so only a message crosses).
    const escapeForward = opts?.printViaParent
        ? "<script>document.addEventListener('keydown',function(e){if(e.key==='Escape'&&window.parent&&window.parent!==window){window.parent.postMessage({type:'case-report-escape'},'*')}})</script>"
        : '';
    return `${escapeForward}<div class="toolbar">
        <span class="toolbar-title">${esc(title)}</span>
        <div class="toolbar-actions">
            <button type="button" class="btn-shell screen-only" onclick="window.__reportExpandAll && window.__reportExpandAll()">Expand all</button>
            <button type="button" class="btn-shell screen-only" onclick="window.__reportCollapseAll && window.__reportCollapseAll()">Collapse all</button>${saves}
            <button type="button" class="btn-print" onclick="${printCall}">Print / Save as PDF</button>
        </div>
    </div>`;
}

/** Sticky left-rail TOC (screen). Also emits a print-only flat TOC list. */
export function buildReportToc(entries: ReportTocEntry[]): string {
    if (!entries.length) return '';
    const links = entries.map((e) =>
        `<a href="#${esc(e.id)}">${esc(e.label)}</a>`).join('');
    const printItems = entries.map((e) =>
        `<li><a href="#${esc(e.id)}">${esc(e.label)}</a></li>`).join('');
    return `<aside class="report-toc-rail" data-report-toc>
        <div class="toc-label">On this page</div>
        <nav>${links}</nav>
    </aside>
    <div class="toc print-toc"><h2>Table of Contents</h2><ol>${printItems}</ol></div>`;
}

export interface ReportMastheadOpts {
    title: string;
    subtitle?: string;
    dataMode?: 'mock' | 'cached' | 'live' | string | null;
    brand?: string;
}

/** Officer-facing document masthead (preferred over plain centered header for new reports). */
export function buildReportMasthead(opts: ReportMastheadOpts): string {
    const now = new Date();
    const dateStr = now.toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' });
    const timeStr = now.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
    const brand = opts.brand || 'Outris · Crypto Investigation';
    const mode = opts.dataMode
        ? `<span class="data-mode ${esc(String(opts.dataMode))}">${esc(String(opts.dataMode))}</span>`
        : '';
    return `<div class="masthead">
        <div class="brand">${esc(brand)}</div>
        <h1>${esc(opts.title)}</h1>
        ${opts.subtitle ? `<div class="subtitle">${esc(opts.subtitle)}</div>` : ''}
        <div class="date">Generated: ${esc(dateStr)} at ${esc(timeStr)}</div>
        ${mode}
    </div>`;
}

/** Collapsible report section (open by default; print expands all). */
export function wrapReportSection(
    id: string,
    title: string,
    bodyHtml: string,
    opts?: { open?: boolean },
): string {
    const openAttr = opts?.open === false ? '' : ' open';
    return `<details class="report-section" id="${esc(id)}"${openAttr}>
        <summary class="report-section-summary"><span class="report-section-title">${esc(title)}</span></summary>
        <div class="report-section-body">${bodyHtml}</div>
    </details>`;
}

/** Wrap main column + optional TOC rail. */
/** Legal footer for generated LE PDFs / printables. */
export const OUTRIS_REPORT_FOOTER_COPY = 'Powered by Outris  ·  © 2026';

export function buildOutrisBrandWatermark(): string {
    return '<div class="outris-brand-watermark" aria-hidden="true">OUTRIS</div>';
}

export function buildOutrisReportFooter(): string {
    return `<footer class="outris-report-footer" data-testid="outris-report-footer">${OUTRIS_REPORT_FOOTER_COPY}</footer>`;
}

export function buildReportShell(mainHtml: string, tocEntries?: ReportTocEntry[]): string {
    const toc = tocEntries?.length ? buildReportToc(tocEntries) : '';
    const brand = buildOutrisBrandWatermark();
    const footer = buildOutrisReportFooter();
    if (!toc) {
        return `${brand}<div class="report-shell"><div class="report-main">${mainHtml}${footer}</div></div>`;
    }
    return `${brand}<div class="report-shell"><div class="report-layout">${toc}<div class="report-main">${mainHtml}${footer}</div></div></div>`;
}

/** Build the centered report header with title, subtitle, and generated timestamp */
export function buildReportHeader(title: string, subtitle: string): string {
    const now = new Date();
    const dateStr = now.toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' });
    const timeStr = now.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
    return `<div class="header">
        <h1>${esc(title)}</h1>
        <div class="subtitle">${esc(subtitle)}</div>
        <div class="date">Generated: ${dateStr} at ${timeStr}</div>
    </div>`;
}

/** Open a complete HTML string in a new browser tab */
export function openReportHtmlInNewTab(html: string): void {
    const win = window.open('', '_blank');
    if (!win) {
        alert('Pop-up blocked. Please allow pop-ups for this site to view the report.');
        return;
    }
    win.document.write(html);
    win.document.close();
}

/** Wrap body content into a full HTML document with embedded CSS + shell script */
export function wrapReportHtml(body: string, title: string, extraCss?: string): string {
    return `<!DOCTYPE html><html lang="en"><head><meta charset="utf-8"/><meta name="viewport" content="width=device-width, initial-scale=1"/>`
        + `<title>${esc(title)}</title><style>${REPORT_CSS}${extraCss || ''}</style></head>`
        + `<body>${body}<script>${REPORT_SHELL_SCRIPT}</script></body></html>`;
}

/**
 * One line block for the transaction fetch summary (NS-38): rows hidden as dust and rows excluded from totals
 * because the ingest stamped them counterfeit-token or look-alike-sender. Empty when the rows hold none.
 */
export function renderStampedRowsLine(txs: readonly any[]): string {
    const line = stampedRowsLine(txs);
    return line
        ? `<p data-testid="report-stamped-rows" style="font-size:11px;color:#92400e;margin:6px 0 0;">${esc(line)}.</p>`
        : '';
}

/** Marked list of counterparties the triage left out because they are stamped (same badge text as the Inspector). */
export function renderStampedCounterparties(list: readonly any[] | null | undefined): string {
    const items = Array.isArray(list) ? list : [];
    if (items.length === 0) return '';
    let html = '<p style="font-size:11px;color:#92400e;margin:6px 0 2px;">Left out of the ranking and the totals:</p><ul style="font-size:11px;margin:0 0 6px 16px;">';
    for (const c of items.slice(0, 20)) {
        const badge = stampBadgeText({
            counterfeit: c.stamp === 'counterfeit_token',
            poisoning_candidate: c.stamp === 'look_alike_sender',
            counterfeit_claimed_value: Object.values(c.claimed || {})[0],
            counterfeit_as: Object.keys(c.claimed || {})[0],
        });
        html += `<li><span class="mono">${esc(String(c.address || '?'))}</span> ${badge ? `<strong>${esc(badge)}</strong>` : ''}</li>`;
    }
    html += '</ul>';
    return html;
}
